import { createContext, useContext, useEffect, useState, type ReactNode, useCallback, useMemo, useRef } from 'react';
import { User } from '../types/auth.js';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User & { token: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  // Add debug logging for user state changes
  useEffect(() => {
    console.log('[Web AuthContext] User state changed:', user);
  }, [user]);
  
  // Check for existing auth state on initial load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        try {
          // Set the default authorization header for axios
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Parse user data
          const user = JSON.parse(userData);
          
          // Verify the token is still valid
          try {
            // You might want to add an API endpoint to validate the token
            // For now, we'll just set the user if we have valid data
            if (user.id && user.email) {
              setUser(user);
              console.log('[Web AuthContext] Restored user from localStorage:', user);
            } else {
              throw new Error('[Web AuthContext] Invalid user data in localStorage');
            }
          } catch (error) {
            console.error('[Web AuthContext] Error validating token:', error);
            // Clear invalid data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete api.defaults.headers.common['Authorization'];
          }
        } catch (error) {
          console.error('[Web AuthContext] Error parsing user data from localStorage:', error);
          // Clear corrupted data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } else {
        // Clear any partial data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
      }
    };

    initializeAuth();
  }, []);

  
  const login = async (userData: User & { token: string }): Promise<boolean> => {
    try {
      console.log('[Web AuthContext] login called with user:', userData);
      
      if (!userData.token) {
        console.error('[Web AuthContext] No token provided in login response');
        return false;
      }
      
      try {
        // Store token in localStorage
        localStorage.setItem('token', userData.token);
        
        // Set the default authorization header for axios
        api.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
        
        // We already have the user data from the login response, no need to fetch again
        const { token, ...userWithoutToken } = userData;
        
        // Ensure we have the required user data
        if (!userWithoutToken.id || !userWithoutToken.email) {
          throw new Error('[Web AuthContext] Invalid user data in login response');
        }
        
        // Update the user state
        setUser(userWithoutToken);
        
        // Store user data in localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userWithoutToken));
        
        console.log('[Web AuthContext] User set in context:', userWithoutToken);
        return true;
      } catch (error) {
        console.error('[Web AuthContext] Error processing login response:', error);
        // Clean up on error
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error('[Web AuthContext] Error in AuthContext login:', error);
      // Clean up on error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      return false;
    }
  };
  
  const logout = useCallback(async () => {
    try {
      // Clear the token from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Clear the axios authorization header
      delete api.defaults.headers.common['Authorization'];
      
      // Clear the user state
      setUser(null);
      
      // Navigate to login page
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('[Web AuthContext] Error during logout:', error);
      // Even if there's an error, we should still try to clear the state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);
  
  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
  }, []);

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
    setUser,
  }), [user, isLoading, login, logout, updateUser, setUser]);
  
  // Only render children after auth is initialized

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('[Web AuthContext] useAuth must be used within an AuthProvider');
  }
  return context;
};
